import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguageStore } from '../stores/languageStore';
import { useAuthStore } from '../stores/authStore';
import { useCaseTextStore } from '../stores/caseTextStore';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { parseLawsuitWizardParams, updateLawsuitWizardURL } from '../utils/urlParams';
import { appendFileToFormData } from '../utils/fileEncoding';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { Lawsuit, LawsuitStatus } from '@tvia-ai/shared';
import { useAnalytics, useComponentAnalytics } from '../hooks/useAnalytics';

// Step components
import CaseDetailsStep from '../components/lawsuit/CaseDetailsStep';
import AIAnalysisStep from '../components/lawsuit/AIAnalysisStep';
import DocumentGenerationStep from '../components/lawsuit/DocumentGenerationStep';
import VerificationStep from '../components/lawsuit/VerificationStep';

const STEPS = [
  'caseDetails',
  'aiAnalysis',
  'verification',
  'documentGeneration'
] as const;

const EDIT_STEPS = [
  'verification',
  'documentGeneration'
] as const;

type Step = typeof STEPS[number];

const LawsuitFilingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isRTL } = useLanguageStore();
  const { user } = useAuthStore();
  const { getAndClearCaseText } = useCaseTextStore();
  const analytics = useAnalytics();
  
  // Track component usage
  useComponentAnalytics('LawsuitFilingPage');
  
  const [currentStep, setCurrentStep] = useState<Step>('caseDetails');
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<Lawsuit>({
    id: '',
    title: '',
    userStory: '',
    category: 'other',
    country: 'US',
    status: LawsuitStatus.Draft,
    createdBy: user?.id || '',
    participants: [],
    isClassAction: true, // Default to true - beneficial for users
    documents: [],
    files: [], // User-uploaded files
    cost: 9900,
    isPaid: false,
    createdAt: new Date(),
    updatedAt: new Date(),
    userInfo: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      country: 'US',
    },
  });
  
  const [lawsuitId, setLawsuitId] = useState<string | null>(null);
  const [isLoadingLawsuit, setIsLoadingLawsuit] = useState(false);
  const [hasFetchedLawsuit, setHasFetchedLawsuit] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Initialize with preserved case text
  useEffect(() => {
    const preservedText = getAndClearCaseText();
    if (preservedText && !formData.userStory) {
      setFormData(prev => ({
        ...prev,
        userStory: preservedText
      }));
    }
  }, [getAndClearCaseText]);

  // Fetch lawsuit data mutation
  const fetchLawsuitMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.get(`/api/lawsuits/${id}`);
      return response.data;
    },
  });

  // Add mutation for creating a lawsuit with files
  const createLawsuitMutation = useMutation({
    mutationFn: async (data: Lawsuit) => {
      const formDataToSend = new FormData();
      // Append all fields except files
      if (data.title) formDataToSend.append('title', data.title);
      formDataToSend.append('userStory', data.userStory || '');
      formDataToSend.append('category', data.category || 'other');
      formDataToSend.append('country', data.country || 'US');
      formDataToSend.append('isClassAction', String(data.isClassAction));
      if (data.userInfo) {
        formDataToSend.append('userInfo', JSON.stringify(data.userInfo));
      }
      // Append files if present with UTF-8 safe encoding
      if (Array.isArray(uploadedFiles)) {
        uploadedFiles.forEach((file: File) => {
          appendFileToFormData(formDataToSend, 'files', file);
        });
      }
      const response = await axios.post('/api/lawsuits', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  });

  // Handle URL parameters for step and lawsuitId
  useEffect(() => {
    const params = parseLawsuitWizardParams(searchParams);
    
    // Set lawsuitId from URL if provided
    if (params.lawsuitId) {
      setLawsuitId(params.lawsuitId);
      setFormData((prev: Lawsuit) => ({ ...prev, id: params.lawsuitId || '' }));
      
      // Check if this is edit mode (existing lawsuit starting from verification)
      if (params.step === 'verification') {
        setIsEditMode(true);
      }
      
      // Track continuing existing lawsuit
      analytics.trackLawsuit.viewDetails(params.lawsuitId, formData.category);
    } else {
      // Track starting new lawsuit filing
      analytics.trackLawsuit.startFiling(formData.category, formData.country);
    }
    
    // Set step from URL if provided and valid
    if (params.step && STEPS.includes(params.step as Step)) {
      setCurrentStep(params.step as Step);
    }
  }, [searchParams]);

  // Fetch lawsuit data if we have lawsuitId but no lawsuit data
  useEffect(() => {
    const fetchLawsuitData = async () => {
      if (lawsuitId && !hasFetchedLawsuit && !isLoadingLawsuit) {
        setIsLoadingLawsuit(true);
        setHasFetchedLawsuit(true);
        try {
          const result = await fetchLawsuitMutation.mutateAsync(lawsuitId);
          const lawsuitData = result.data;
          
          // Update form data with fetched lawsuit data
          setFormData((prev: Lawsuit) => ({
            ...prev,
            ...lawsuitData.lawsuit,
            id: lawsuitId,
          }));
        } catch (error) {
          console.error('Failed to fetch lawsuit data:', error);
          // If fetch fails, we might be creating a new lawsuit
        } finally {
          setIsLoadingLawsuit(false);
        }
      }
    };

    fetchLawsuitData();
  }, [lawsuitId, hasFetchedLawsuit, isLoadingLawsuit]);

  // Update URL when step or lawsuitId changes
  useEffect(() => {
    updateLawsuitWizardURL({
      step: currentStep,
      lawsuitId: lawsuitId || undefined,
    });
  }, [currentStep, lawsuitId]);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const updateFormData = (updates: Partial<Lawsuit>, files?: File[]) => {
    setFormData((prev: Lawsuit) => ({ ...prev, ...updates }));
    if (files) setUploadedFiles(files);
  };

  const nextStep = () => {
    const stepsToUse = isEditMode ? EDIT_STEPS : STEPS;
    const currentIndex = stepsToUse.indexOf(currentStep as any);
    if (currentIndex < stepsToUse.length - 1) {
      setCurrentStep(stepsToUse[currentIndex + 1]);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    // In edit mode, prevent going backwards
    if (isEditMode) {
      return;
    }

    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
      window.scrollTo(0, 0);
    }
  };

  const getStepNumber = (step: Step): number => {
    const stepsToUse = isEditMode ? EDIT_STEPS : STEPS;
    return stepsToUse.indexOf(step as any) + 1;
  };

  const isStepCompleted = (step: Step): boolean => {
    const stepsToUse = isEditMode ? EDIT_STEPS : STEPS;
    const currentIndex = stepsToUse.indexOf(currentStep as any);
    const stepIndex = stepsToUse.indexOf(step as any);
    return stepIndex < currentIndex;
  };

  const isStepActive = (step: Step): boolean => {
    return step === currentStep;
  };

  // Helper: get steps in correct order for LTR/RTL
  const getDisplaySteps = () => {
    const stepsToUse = isEditMode ? EDIT_STEPS : STEPS;
    return isRTL ? [...stepsToUse].reverse() : stepsToUse;
  };

  const handleCaseDetailsNext = async () => {
    if (lawsuitId) {
      // Lawsuit already exists, just move to next step
      nextStep();
      return;
    }
    try {
      const result = await createLawsuitMutation.mutateAsync(formData);
      const newLawsuit = result.data.lawsuit;
      setLawsuitId(newLawsuit.id);
      setFormData((prev: Lawsuit) => ({ ...prev, ...newLawsuit }));
      nextStep();
    } catch (error) {
      // TODO: handle error UI
      console.error('Failed to create lawsuit:', error);
    }
  };

  if (!user || isLoadingLawsuit) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-4 sm:py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {isEditMode ? t('filing.editTitle') : t('filing.title')}
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            {isEditMode
              ? t('filing.editSubtitle')
              : t('filing.subtitle')
            }
          </p>
          {isEditMode && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 mx-auto max-w-2xl">
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                                 <p className="text-sm text-blue-800">
                   {t('filing.editModeMessage')}
                 </p>
              </div>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="mb-6 sm:mb-8">
          <nav aria-label="Progress">
            <ol
              className={`flex flex-wrap items-center justify-center gap-2 sm:gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {getDisplaySteps().map((step, idx, arr) => {
                // For RTL, index 0 is the rightmost (step 1), so next step is idx-1
                // For LTR, index 0 is the leftmost (step 1), so next step is idx+1
                const nextStep = isRTL ? arr[idx - 1] : arr[idx + 1];
                const showLine = isRTL ? idx > 0 : idx < arr.length - 1;
                return (
                  <li key={step} className="flex items-center">
                    <div className="flex flex-col sm:flex-row items-center">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 flex-shrink-0 ${
                          isStepCompleted(step)
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : isStepActive(step)
                            ? 'border-blue-600 text-blue-600'
                            : 'border-gray-300 text-gray-400'
                        }`}
                      >
                        {isStepCompleted(step) ? (
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <span className="text-sm font-medium">{getStepNumber(step)}</span>
                        )}
                      </div>
                      <span className={`mt-1 sm:mt-0 ${isRTL ? 'sm:mr-2' : 'sm:ml-2'} text-xs sm:text-sm font-medium text-center sm:text-left ${
                        isStepActive(step) ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {t(`filing.steps.${step}`)}
                      </span>
                    </div>
                    {showLine && (
                      <div
                        className={`hidden sm:block h-0.5 w-4 sm:w-8 rounded ${isRTL ? 'mr-2' : 'ml-2'} ${
                          isStepCompleted(nextStep) ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          {currentStep === 'caseDetails' && (
            <CaseDetailsStep
              data={formData}
              onUpdate={updateFormData}
              onNext={handleCaseDetailsNext}
            />
          )}
          
          {currentStep === 'aiAnalysis' && (
            <AIAnalysisStep
              key="aiAnalysis"
              data={formData}
              onUpdate={updateFormData}
              onNext={nextStep}
              onBack={prevStep}
              onAnalysisComplete={(analysis, lawsuitIdFromAnalysis) => {
                setLawsuitId(lawsuitIdFromAnalysis || lawsuitId);
                setFormData((prev: Lawsuit) => ({
                  ...prev,
                  id: lawsuitIdFromAnalysis || lawsuitId || '',
                  aiAnalysis: analysis,
                }));
                // Removed nextStep() here to allow user to answer follow-up questions
              }}
            />
          )}
          
          {currentStep === 'verification' && (
            <VerificationStep
              data={formData}
              user={user}
              onUpdate={updateFormData}
              onNext={nextStep}
              onBack={isEditMode ? () => {} : prevStep}
              isEditMode={isEditMode}
            />
          )}
          
          {currentStep === 'documentGeneration' && (
            <DocumentGenerationStep
              lawsuitId={lawsuitId!}
              onBack={prevStep}
              onDocumentsGenerated={() => {}} // Removed onDocumentsGenerated prop
              isPremiumMode={formData.aiAnalysis?.isPremiumAnalysis || false}
              onUpgradeToPremium={() => {
                // Navigate to premium upgrade page or show upgrade modal
                window.location.href = '/pricing';
              }}
              isEditMode={isEditMode}
            />
          )}
          
        </div>

        {/* Navigation Buttons */}
        {!isEditMode && currentStep !== STEPS[0] as Step && (
          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-6">
            <button
              onClick={prevStep}
              disabled={currentStep === STEPS[0] as Step}
              className={`px-6 py-3 sm:py-2 rounded-md text-sm font-medium min-h-[44px] ${
                currentStep === STEPS[0] as Step
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('common.back')}
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 sm:py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 min-h-[44px]"
            >
              {t('common.cancel')}
            </button>
          </div>
        )}

        {/* Edit Mode Navigation */}
        {isEditMode && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => navigate(`/lawsuit/${lawsuitId}`)}
              className="px-6 py-3 sm:py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 min-h-[44px]"
            >
              {t('common.returnToLawsuit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawsuitFilingPage;